import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { veterinarioService } from '../services/veterinarioService'
import { clinicaService } from '../services/clinicaService'
import Table, { TableHeader, TableBody, TableRow, TableCell, TableHead } from '../components/ui/Table'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Listbox from '../components/ui/Listbox'
import Disclosure from '../components/ui/Disclosure'
import Card from '../components/ui/Card'
import Badge from '../components/ui/Badge'
import LoadingState from '../components/ui/LoadingState'
import EmptyState from '../components/ui/EmptyState'
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../components/ui/Modal'
import Tabs, { TabList, TabItem, TabPanels, TabPanel } from '../components/ui/Tabs'
import { User, Plus, Search, RefreshCw, AlertCircle, Edit, Trash2, Eye, Building2, X, Mail, Key, Phone, UserCheck, Users } from 'lucide-react'
import Avatar from '../components/ui/Avatar'

export default function Veterinarios() {
  const { user } = useAuth()
  const [veterinarios, setVeterinarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useState({
    search: '',
    id_clinica: '',
    especialidad: ''
  })
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  })
  const [clinicas, setClinicas] = useState([])
  
  // Estados para tabs y veterinarios disponibles
  const [activeTab, setActiveTab] = useState(0)
  const [veterinariosDisponibles, setVeterinariosDisponibles] = useState([])
  const [loadingDisponibles, setLoadingDisponibles] = useState(false)
  const [filtrosDisponibles, setFiltrosDisponibles] = useState({
    search: '',
    especialidad: ''
  })
  const [paginationDisponibles, setPaginationDisponibles] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  })
  
  // Estados para modal y formulario
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    apellido: '',
    cedula: '',
    telefono: '',
    id_clinica: '',
    matricula_profesional: '',
    especialidad: '',
    cargo: 'VETERINARIO',
    es_admin_clinica: false
  })
  const [formErrors, setFormErrors] = useState({})

  const { isVeterinarioAdmin, userClinicaId, canCreateVeterinario, canManageVeterinario, isAdmin } = usePermissions()

  useEffect(() => {
    fetchVeterinarios()
    fetchClinicas()
  }, [pagination.current_page, filtros])

  useEffect(() => {
    if (activeTab === 1 && canCreateVeterinario) {
      fetchVeterinariosDisponibles()
    }
  }, [activeTab, paginationDisponibles.current_page, filtrosDisponibles])

  const fetchVeterinarios = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params = {
        per_page: pagination.per_page,
        page: pagination.current_page,
        ...(filtros.search && { search: filtros.search }),
        ...(filtros.id_clinica && { id_clinica: filtros.id_clinica }),
        ...(filtros.especialidad && { especialidad: filtros.especialidad })
      }

      const response = await veterinarioService.getAll(params)
      
      if (response.success) {
        if (response.data.data) {
          setVeterinarios(response.data.data)
          setPagination({
            current_page: response.data.current_page || 1,
            per_page: response.data.per_page || 15,
            total: response.data.total || 0,
            last_page: response.data.last_page || 1
          })
        } else {
          setVeterinarios(Array.isArray(response.data) ? response.data : [])
        }
      } else {
        setError(response.error || 'Error al cargar veterinarios')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
      console.error('Error fetching veterinarios:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchClinicas = async () => {
    try {
      // Si es veterinario admin, solo obtener su clínica
      const params = { per_page: 100 }
      if (isVeterinarioAdmin && userClinicaId) {
        params.id_clinica = userClinicaId
      }
      const response = await clinicaService.getAll(params)
      if (response.success) {
        const clinicasData = response.data.data || response.data || []
        setClinicas(clinicasData)
        // Si es veterinario admin y solo hay una clínica, establecerla automáticamente en filtros
        if (isVeterinarioAdmin && clinicasData.length === 1 && !filtros.id_clinica) {
          setFiltros(prev => ({ ...prev, id_clinica: clinicasData[0].id_clinica || clinicasData[0].idClinica }))
        }
      }
    } catch (err) {
      console.error('Error fetching clínicas:', err)
    }
  }

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      nombre: '',
      apellido: '',
      cedula: '',
      telefono: '',
      id_clinica: isVeterinarioAdmin ? userClinicaId : '',
      matricula_profesional: '',
      especialidad: '',
      cargo: 'VETERINARIO',
      es_admin_clinica: false
    })
    setFormErrors({})
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setFormErrors({})
    setError(null)

    try {
      // Preparar datos para enviar
      const dataToSend = {
        ...formData,
        id_clinica: isVeterinarioAdmin ? userClinicaId : formData.id_clinica,
        es_admin_clinica: isVeterinarioAdmin ? false : formData.es_admin_clinica
      }

      const response = await veterinarioService.create(dataToSend)
      
      if (response.success) {
        setShowCreateModal(false)
        resetForm()
        fetchVeterinarios()
      } else {
        // Manejar errores de validación
        if (response.details) {
          setFormErrors(response.details)
        } else {
          setError(response.error || 'Error al crear veterinario')
        }
      }
    } catch (err) {
      if (err.response?.data?.details) {
        setFormErrors(err.response.data.details)
      } else {
        setError(err.response?.data?.error || err.message || 'Error al crear veterinario')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const fetchVeterinariosDisponibles = async () => {
    try {
      setLoadingDisponibles(true)
      setError(null)
      
      const params = {
        per_page: paginationDisponibles.per_page,
        page: paginationDisponibles.current_page,
        ...(filtrosDisponibles.search && { search: filtrosDisponibles.search }),
        ...(filtrosDisponibles.especialidad && { especialidad: filtrosDisponibles.especialidad })
      }

      const response = await veterinarioService.getDisponibles(params)
      
      if (response.success) {
        if (response.data.data) {
          setVeterinariosDisponibles(response.data.data)
          setPaginationDisponibles({
            current_page: response.data.current_page || 1,
            per_page: response.data.per_page || 15,
            total: response.data.total || 0,
            last_page: response.data.last_page || 1
          })
        } else {
          setVeterinariosDisponibles(Array.isArray(response.data) ? response.data : [])
        }
      } else {
        setError(response.error || 'Error al cargar veterinarios disponibles')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
      console.error('Error fetching veterinarios disponibles:', err)
    } finally {
      setLoadingDisponibles(false)
    }
  }

  const handleContratar = async (id) => {
    if (!confirm('¿Estás seguro de que quieres contratar a este veterinario en tu clínica?')) {
      return
    }

    try {
      const dataToSend = isVeterinarioAdmin 
        ? {} // El backend usará automáticamente la clínica del admin
        : { id_clinica: formData.id_clinica || userClinicaId }

      const response = await veterinarioService.contratar(id, dataToSend)
      
      if (response.success) {
        fetchVeterinariosDisponibles()
        fetchVeterinarios() // Actualizar también la lista de la clínica
        setError(null)
      } else {
        setError(response.error || 'Error al contratar veterinario')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al contratar veterinario')
    }
  }

  const handleDelete = async (id) => {
    const confirmMessage = isVeterinarioAdmin
      ? '¿Estás seguro de que quieres dar de baja a este veterinario? Quedará dado de baja en esta clínica y podrá ser contratado por otras.'
      : '¿Estás seguro de que quieres desactivar este veterinario?'
    
    if (!confirm(confirmMessage)) {
      return
    }

    try {
      const response = await veterinarioService.delete(id)
      if (response.success) {
        fetchVeterinarios()
        // Mostrar mensaje de éxito
        setError(null)
        // Podríamos mostrar un mensaje de éxito aquí si tuviéramos un sistema de notificaciones
      } else {
        setError(response.error || 'Error al desactivar veterinario')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al desactivar veterinario')
    }
  }

  const especialidadesUnicas = [...new Set(veterinarios.map(v => v.especialidad).filter(Boolean))]

  const clearFilters = () => {
    setFiltros({ search: '', id_clinica: '', especialidad: '' })
  }

  const hasActiveFilters = filtros.search || filtros.id_clinica || filtros.especialidad

  if (loading && veterinarios.length === 0) {
    return <LoadingState message="Cargando veterinarios..." size="lg" />
  }

  const especialidadesDisponibles = [...new Set(veterinariosDisponibles.map(v => v.especialidad).filter(Boolean))]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Veterinarios</h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            {isVeterinarioAdmin 
              ? 'Gestión de veterinarios de tu clínica' 
              : 'Gestión de veterinarios del sistema'}
          </p>
        </div>
        {canCreateVeterinario && activeTab === 0 && (
          <Button onClick={() => {
            resetForm()
            setShowCreateModal(true)
          }} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Veterinario
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
          <button onClick={() => setError(null)} className="ml-4">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Tabs */}
      {canCreateVeterinario && (
        <Tabs selectedIndex={activeTab} onChange={setActiveTab}>
          <TabList className="border-b border-gray-200 dark:border-gray-700">
            <TabItem className="flex items-center space-x-2">
              <Users className="w-5 h-5" />
              <span>En la clínica</span>
            </TabItem>
            <TabItem className="flex items-center space-x-2">
              <UserCheck className="w-5 h-5" />
              <span>Veterinarios Disponibles</span>
            </TabItem>
          </TabList>

          <TabPanels className="mt-6">
            {/* Tab 1: Veterinarios dados de alta en la clínica */}
            <TabPanel>
              {/* Filtros */}
      {!isVeterinarioAdmin && (
        <Disclosure 
          title="Filtros de Búsqueda"
          defaultOpen={hasActiveFilters}
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
        >
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input
                label="Búsqueda"
                leftIcon={Search}
                placeholder="Nombre, matrícula..."
                value={filtros.search}
                onChange={(e) => setFiltros({ ...filtros, search: e.target.value })}
              />
              <Listbox
                label="Clínica"
                options={[
                  { value: '', label: 'Todas' },
                  ...clinicas.map((clinica) => ({
                    value: clinica.id_clinica || clinica.idClinica,
                    label: clinica.nombre
                  }))
                ]}
                value={filtros.id_clinica}
                onChange={(value) => setFiltros({ ...filtros, id_clinica: value })}
              />
              <Listbox
                label="Especialidad"
                options={[
                  { value: '', label: 'Todas' },
                  ...especialidadesUnicas.map((especialidad) => ({
                    value: especialidad,
                    label: especialidad
                  }))
                ]}
                value={filtros.especialidad}
                onChange={(value) => setFiltros({ ...filtros, especialidad: value })}
              />
              <div className="flex items-end">
                <Button onClick={fetchVeterinarios} variant="secondary" className="w-full">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualizar
                </Button>
              </div>
            </div>
            {hasActiveFilters && (
              <div className="flex justify-end">
                <Button variant="secondary" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Limpiar Filtros
                </Button>
              </div>
            )}
          </div>
        </Disclosure>
      )}
      
      {/* Filtros simplificados para veterinario admin */}
      {isVeterinarioAdmin && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Búsqueda"
              leftIcon={Search}
              placeholder="Nombre, matrícula..."
              value={filtros.search}
              onChange={(e) => setFiltros({ ...filtros, search: e.target.value })}
            />
            <Listbox
              label="Especialidad"
              options={[
                { value: '', label: 'Todas' },
                ...especialidadesUnicas.map((especialidad) => ({
                  value: especialidad,
                  label: especialidad
                }))
              ]}
              value={filtros.especialidad}
              onChange={(value) => setFiltros({ ...filtros, especialidad: value })}
            />
            <div className="flex items-end">
              <Button onClick={fetchVeterinarios} variant="secondary" className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tabla */}
      {veterinarios.length === 0 ? (
        <EmptyState
          icon={User}
          title="No hay veterinarios registrados"
          description="No se encontraron veterinarios con los filtros seleccionados."
        />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="hidden md:table-cell">Email</TableHead>
                  <TableHead className="hidden lg:table-cell">Clínica</TableHead>
                  <TableHead className="hidden xl:table-cell">Matrícula</TableHead>
                  <TableHead className="hidden md:table-cell">Especialidad</TableHead>
                  <TableHead className="hidden lg:table-cell">Teléfono</TableHead>
                  <TableHead>En la clínica</TableHead>
                  {canManageVeterinario && (
                    <TableHead>Acciones</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {veterinarios.map((veterinario) => (
                  <TableRow key={veterinario.id_veterinario || veterinario.idVeterinario}>
                    <TableCell className="font-medium">
                      {veterinario.usuario ? (
                        <div className="flex items-center space-x-3">
                          <Avatar user={veterinario.usuario} size="sm" />
                          <span>{`${veterinario.usuario.nombre} ${veterinario.usuario.apellido || ''}`.trim()}</span>
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{veterinario.usuario ? veterinario.usuario.email : '-'}</TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {veterinario.clinica ? (
                        <div className="flex items-center space-x-1">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span>{veterinario.clinica.nombre || veterinario.clinica.name || '-'}</span>
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">{veterinario.matricula_profesional || veterinario.matriculaProfesional || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {veterinario.especialidad ? (
                        <Badge variant="info">{veterinario.especialidad}</Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">{veterinario.usuario ? veterinario.usuario.telefono || '-' : '-'}</TableCell>
                    <TableCell>
                      <Badge variant={veterinario.activo ? 'success' : 'danger'}>
                        {veterinario.activo ? 'Sí' : 'De baja'}
                      </Badge>
                    </TableCell>
                    {canManageVeterinario && (
                      <TableCell>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {}}
                            className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
                            title="Ver detalles"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => {}}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                            title="Editar"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          {/* Mostrar botón de desactivar si no es el mismo veterinario */}
                          {((veterinario.id_veterinario || veterinario.idVeterinario) !== (user?.veterinarios?.[0]?.id_veterinario || user?.veterinarios?.[0]?.idVeterinario)) && (
                            <button
                              onClick={() => handleDelete(veterinario.id_veterinario || veterinario.idVeterinario)}
                              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                              title="Desactivar"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

              {/* Paginación */}
              {pagination.last_page > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Mostrando <span className="font-medium">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> a{' '}
                    <span className="font-medium">{Math.min(pagination.current_page * pagination.per_page, pagination.total)}</span> de{' '}
                    <span className="font-medium">{pagination.total}</span> resultados
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={pagination.current_page === 1}
                      onClick={() => setPagination({ ...pagination, current_page: pagination.current_page - 1 })}
                    >
                      Anterior
                    </Button>
                    <div className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                      Página {pagination.current_page} de {pagination.last_page}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={pagination.current_page === pagination.last_page}
                      onClick={() => setPagination({ ...pagination, current_page: pagination.current_page + 1 })}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </TabPanel>

            {/* Tab 2: Veterinarios Disponibles */}
            <TabPanel>
              {loadingDisponibles && veterinariosDisponibles.length === 0 ? (
                <LoadingState message="Cargando veterinarios disponibles..." size="lg" />
              ) : (
                <>
                  {/* Filtros para disponibles */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Input
                        label="Búsqueda"
                        leftIcon={Search}
                        placeholder="Nombre, email, matrícula..."
                        value={filtrosDisponibles.search}
                        onChange={(e) => setFiltrosDisponibles({ ...filtrosDisponibles, search: e.target.value })}
                      />
                      <Listbox
                        label="Especialidad"
                        options={[
                          { value: '', label: 'Todas' },
                          ...especialidadesDisponibles.map((especialidad) => ({
                            value: especialidad,
                            label: especialidad
                          }))
                        ]}
                        value={filtrosDisponibles.especialidad}
                        onChange={(value) => setFiltrosDisponibles({ ...filtrosDisponibles, especialidad: value })}
                      />
                      <div className="flex items-end">
                        <Button onClick={fetchVeterinariosDisponibles} variant="secondary" className="w-full">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Actualizar
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Tabla de disponibles */}
                  {veterinariosDisponibles.length === 0 ? (
                    <EmptyState
                      icon={UserCheck}
                      title="No hay veterinarios disponibles"
                      description="No se encontraron veterinarios disponibles para contratar."
                    />
                  ) : (
                    <Card>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Nombre</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Clínica Anterior</TableHead>
                              <TableHead>Matrícula</TableHead>
                              <TableHead>Especialidad</TableHead>
                              <TableHead>Teléfono</TableHead>
                              <TableHead>Acciones</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {veterinariosDisponibles.map((veterinario) => (
                              <TableRow key={veterinario.id_veterinario || veterinario.idVeterinario}>
                                <TableCell className="font-medium">
                                  {veterinario.usuario 
                                    ? `${veterinario.usuario.nombre} ${veterinario.usuario.apellido || ''}`.trim()
                                    : '-'}
                                </TableCell>
                                <TableCell>{veterinario.usuario ? veterinario.usuario.email : '-'}</TableCell>
                                <TableCell>
                                  {veterinario.clinica ? (
                                    <div className="flex items-center space-x-1">
                                      <Building2 className="w-4 h-4 text-gray-400" />
                                      <span>{veterinario.clinica.nombre || veterinario.clinica.name || '-'}</span>
                                    </div>
                                  ) : (
                                    <Badge variant="secondary">Sin clínica</Badge>
                                  )}
                                </TableCell>
                                <TableCell>{veterinario.matricula_profesional || veterinario.matriculaProfesional || '-'}</TableCell>
                                <TableCell>
                                  {veterinario.especialidad ? (
                                    <Badge variant="info">{veterinario.especialidad}</Badge>
                                  ) : '-'}
                                </TableCell>
                                <TableCell>{veterinario.usuario ? veterinario.usuario.telefono || '-' : '-'}</TableCell>
                                <TableCell>
                                  <Button
                                    onClick={() => handleContratar(veterinario.id_veterinario || veterinario.idVeterinario)}
                                    variant="primary"
                                    size="sm"
                                  >
                                    <UserCheck className="w-4 h-4 mr-2" />
                                    Contratar
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </Card>
                  )}

                  {/* Paginación disponibles */}
                  {paginationDisponibles.last_page > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mt-6">
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        Mostrando <span className="font-medium">{((paginationDisponibles.current_page - 1) * paginationDisponibles.per_page) + 1}</span> a{' '}
                        <span className="font-medium">{Math.min(paginationDisponibles.current_page * paginationDisponibles.per_page, paginationDisponibles.total)}</span> de{' '}
                        <span className="font-medium">{paginationDisponibles.total}</span> resultados
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={paginationDisponibles.current_page === 1}
                          onClick={() => setPaginationDisponibles({ ...paginationDisponibles, current_page: paginationDisponibles.current_page - 1 })}
                        >
                          Anterior
                        </Button>
                        <div className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                          Página {paginationDisponibles.current_page} de {paginationDisponibles.last_page}
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={paginationDisponibles.current_page === paginationDisponibles.last_page}
                          onClick={() => setPaginationDisponibles({ ...paginationDisponibles, current_page: paginationDisponibles.current_page + 1 })}
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}

      {/* Modal Crear Veterinario */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); resetForm(); }} size="lg">
        <ModalHeader onClose={() => { setShowCreateModal(false); resetForm(); }}>
          Nuevo Veterinario
        </ModalHeader>
        <form onSubmit={handleCreate}>
          <ModalContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nombre"
                  required
                  leftIcon={User}
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  error={formErrors.nombre?.[0]}
                />
                <Input
                  label="Apellido"
                  leftIcon={User}
                  value={formData.apellido}
                  onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                  error={formErrors.apellido?.[0]}
                />
              </div>
              <Input
                label="Email"
                type="email"
                required
                leftIcon={Mail}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={formErrors.email?.[0]}
              />
              <Input
                label="Contraseña"
                type="password"
                required
                leftIcon={Key}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                error={formErrors.password?.[0]}
                hint="Mínimo 6 caracteres"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Cédula"
                  value={formData.cedula}
                  onChange={(e) => setFormData({ ...formData, cedula: e.target.value })}
                  error={formErrors.cedula?.[0]}
                />
                <Input
                  label="Teléfono"
                  type="tel"
                  leftIcon={Phone}
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  error={formErrors.telefono?.[0]}
                />
              </div>
              {!isVeterinarioAdmin && (
                <Listbox
                  label="Clínica"
                  required
                  options={[
                    { value: '', label: 'Seleccione una clínica...' },
                    ...clinicas.map((clinica) => ({
                      value: clinica.id_clinica || clinica.idClinica,
                      label: clinica.nombre
                    }))
                  ]}
                  value={formData.id_clinica}
                  onChange={(value) => setFormData({ ...formData, id_clinica: value })}
                  error={formErrors.id_clinica?.[0]}
                />
              )}
              {isVeterinarioAdmin && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <Building2 className="w-4 h-4 inline mr-2" />
                    Clínica: {clinicas.find(c => (c.id_clinica || c.idClinica) === userClinicaId)?.nombre || 'Tu clínica'}
                  </p>
                </div>
              )}
              <Input
                label="Matrícula Profesional"
                required
                value={formData.matricula_profesional}
                onChange={(e) => setFormData({ ...formData, matricula_profesional: e.target.value })}
                error={formErrors.matricula_profesional?.[0]}
                hint="Número único de matrícula profesional"
              />
              <Input
                label="Especialidad"
                required
                value={formData.especialidad}
                onChange={(e) => setFormData({ ...formData, especialidad: e.target.value })}
                error={formErrors.especialidad?.[0]}
                hint="Ej: Cirugía, Medicina General, etc."
              />
              {!isVeterinarioAdmin && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Listbox
                    label="Cargo"
                    options={[
                      { value: 'VETERINARIO', label: 'Veterinario' },
                      { value: 'DIRECTOR', label: 'Director' }
                    ]}
                    value={formData.cargo}
                    onChange={(value) => setFormData({ ...formData, cargo: value })}
                    error={formErrors.cargo?.[0]}
                  />
                  {isAdmin && (
                    <div className="flex items-end">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.es_admin_clinica}
                          onChange={(e) => setFormData({ ...formData, es_admin_clinica: e.target.checked })}
                          className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Administrador de clínica
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button type="submit" loading={submitting}>
              Crear Veterinario
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  )
}
